import { FormControl, MenuItem, Select } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Spinner } from "@/shared/foundation/primitives/Spinner";

import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  type DataTableLoadingState,
} from "../useTable";

import styles from "./DataTablePagination.module.css";

interface PaginationFooterProps {
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  currentPage: number;
  nextPage: () => void;
  prevPage: () => void;
  hasNextPage: boolean;
  canGetPrevPage: boolean;

  /**
   * Total rows across all pages.
   * When `undefined` the row-range label is hidden.
   */
  totalRowCount: number | undefined;

  /**
   * While non-`"none"`, disables the next-page button and swaps the row range
   * for a spinner. Defaults to `"none"`.
   */
  loading?: DataTableLoadingState;
}

/**
 * Footer rendered beneath the AG Grid surface inside `DataTable`.
 * Hosts the page-size selector, the current row range (e.g. `1-25 of 312`),
 * and prev/next page buttons.
 *
 * Internal to the DataTable primitive — consumers don't import this directly;
 * `DataTable` mounts it.
 */
export function PaginationFooter(props: PaginationFooterProps) {
  const {
    hasNextPage,
    canGetPrevPage,
    loading = "none",
    nextPage,
    onPageSizeChange,
    currentPage,
    pageSize,
    prevPage,
    totalRowCount,
  } = props;

  const isFetching = loading !== "none";
  const hasTotal = totalRowCount !== undefined;
  const showRange = hasTotal && !isFetching;

  const rangeStart = totalRowCount === 0 ? 0 : currentPage * pageSize + 1;
  const rangeEnd = hasTotal
    ? Math.min((currentPage + 1) * pageSize, totalRowCount)
    : 0;

  // Defensive fallback if a saved pagesize is outside the options.
  // Better this than a blank value. Sorted to keep ascending order.
  const pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS.includes(
    pageSize as (typeof DEFAULT_PAGE_SIZE_OPTIONS)[number],
  )
    ? DEFAULT_PAGE_SIZE_OPTIONS
    : [...DEFAULT_PAGE_SIZE_OPTIONS, pageSize].sort((a, b) => a - b);

  return (
    <div className={styles.footer}>
      <div className={styles.left}>
        <div className={styles.pageSizeGroup}>
          <span className={styles.label}>Rows per page</span>
          <FormControl size="small">
            <Select<number>
              value={pageSize}
              onChange={(event: SelectChangeEvent<number>) =>
                onPageSizeChange(Number(event.target.value))
              }
              inputProps={{ "aria-label": "Rows per page" }}
              MenuProps={{ className: styles.pageSizeMenu }}
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {hasTotal && isFetching && (
          <span className={styles.counting} role="status" aria-live="polite">
            <Spinner aria-hidden />
            Counting results
          </span>
        )}
        {showRange && (
          <span className={styles.range}>
            {`${rangeStart}-${rangeEnd} of ${totalRowCount}`}
          </span>
        )}
      </div>

      <div className={styles.right}>
        <button
          aria-label="Previous page"
          className={styles.iconButton}
          // Backward navigation always uses an already resolved cursor
          // so no need to gate on isFetching.
          disabled={!canGetPrevPage}
          onClick={prevPage}
        >
          <ChevronLeft />
        </button>
        <button
          aria-label="Next page"
          className={styles.iconButton}
          disabled={!hasNextPage || isFetching}
          onClick={nextPage}
        >
          <ChevronRight />
        </button>
      </div>
    </div>
  );
}
